'use client'

import { JSX } from 'react'

import * as RadioGroup from '@radix-ui/react-radio-group'
import { Controller, FieldValues, Path } from 'react-hook-form'

import { buttonVariants } from '~/components/component-library/Button'
import { cn } from '~/utils/tailwind-utils'

import type { SegmentedControlProps } from '~/components/component-library/SegmentedControl/types'

export type { SegmentedControlProps } from '~/components/component-library/SegmentedControl/types'

export const SegmentedControl = <
  T extends string = string,
  FormType extends FieldValues = FieldValues,
>({
  options,
  control,
  inputName,
  ariaLabel,
  ariaLabelledBy,
}: SegmentedControlProps<T, FormType>): JSX.Element => (
  <Controller
    control={control}
    name={inputName as Path<FormType>}
    render={({ field }) => (
      <RadioGroup.Root
        value={field.value}
        onValueChange={field.onChange}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className="flex w-fit gap-0.5 rounded-xl bg-white p-1 ring-1 ring-inset ring-gray-200 dark:bg-gray-900 dark:ring-gray-700"
      >
        {options.map((option) => (
          <RadioGroup.Item
            key={option.value}
            value={option.value}
            disabled={option.isDisabled}
            className={cn(
              'focus-visible:shadow-none focus-visible:ring-0',
              field.value === option.value && 'pointer-events-none',
              buttonVariants({
                variant: field.value === option.value ? 'primary' : 'tertiary',
                size: 'small',
                isDisabled: option.isDisabled,
              }),
            )}
          >
            {option.icon}
            {option.label}
          </RadioGroup.Item>
        ))}
      </RadioGroup.Root>
    )}
  />
)
